import { useNavigate, useParams } from "react-router-dom";
import ClientShell from "./ClientShell";
import { usePresentationDetail } from "../../lib/presentationQueries";
import OverviewArea from "./presentation/OverviewArea";
import GoalAudienceArea from "./presentation/GoalAudienceArea";
import IdeaBankArea from "./presentation/IdeaBankArea";
import StructureArea from "./presentation/StructureArea";
import SectionsMessageArea from "./presentation/SectionsMessageArea";
import DeliveryLayersArea from "./presentation/DeliveryLayersArea";
import FilesArea from "./presentation/FilesArea";
import VersionHistoryArea from "./presentation/VersionHistoryArea";
import PlaceholderArea from "./presentation/PlaceholderArea";

const AREAS = [
  ["overview", "Overview"], ["goal-audience", "Goal & Audience"], ["idea-bank", "Idea Bank"],
  ["structure", "Structure"], ["message", "Sections & Message"], ["delivery", "Delivery Layers"],
  ["slides", "Slides"], ["performance-map", "Performance Map"], ["files", "Files"], ["versions", "Version History"],
];

export default function PresentationDetail() {
  const { workspaceId, presId, areaKey = "overview" } = useParams();
  const navigate = useNavigate();
  const { data, isPending, isError } = usePresentationDetail(workspaceId, presId);

  if (isError) {
    return (
      <ClientShell>
        <div className="max-w-md text-center mx-auto mt-16" data-testid="presentation-detail-not-found">
          <p className="font-serif text-xl text-ink mb-2">Presentation not found</p>
          <p className="text-ink-soft text-sm mb-4">It may have been removed, or you may not have access to it.</p>
          <button
            onClick={() => navigate(`/rehearsal-room/w/${workspaceId}/presentation`)}
            className="text-sm font-medium text-terracotta hover:text-terracotta/80"
            data-testid="presentation-detail-not-found-back"
          >
            Back to Presentations
          </button>
        </div>
      </ClientShell>
    );
  }

  if (isPending || !data) {
    return (
      <ClientShell>
        <p className="text-ink-soft" data-testid="presentation-detail-loading">Loading presentation…</p>
      </ClientShell>
    );
  }

  const activeStructureVersionId = data.project.active_structure_version_id;

  return (
    <ClientShell>
      <div data-testid="presentation-detail-page">
        <h1 className="font-serif text-2xl text-ink mb-1" data-testid="presentation-detail-title">{data.project.title}</h1>
        <div className="flex items-center gap-2 mb-6 flex-wrap border-b border-ink/10 pb-3" data-testid="presentation-area-tabs">
          {AREAS.map(([key, label]) => (
            <button
              key={key}
              onClick={() => navigate(`/rehearsal-room/w/${workspaceId}/presentation/${presId}/${key}`)}
              className={`text-sm px-3 py-1.5 rounded-full transition-colors ${areaKey === key ? "bg-ink text-paper" : "text-ink-soft hover:bg-ink/5"}`}
              data-testid={`presentation-area-tab-${key}`}
            >
              {label}
            </button>
          ))}
        </div>
        {areaKey === "overview" && <OverviewArea workspaceId={workspaceId} presId={presId} />}
        {areaKey === "goal-audience" && <GoalAudienceArea workspaceId={workspaceId} presId={presId} />}
        {areaKey === "idea-bank" && <IdeaBankArea workspaceId={workspaceId} presId={presId} />}
        {areaKey === "structure" && <StructureArea workspaceId={workspaceId} presId={presId} activeStructureVersionId={activeStructureVersionId} />}
        {areaKey === "message" && <SectionsMessageArea workspaceId={workspaceId} presId={presId} activeStructureVersionId={activeStructureVersionId} />}
        {areaKey === "delivery" && <DeliveryLayersArea workspaceId={workspaceId} presId={presId} activeStructureVersionId={activeStructureVersionId} />}
        {areaKey === "slides" && (
          <PlaceholderArea title="Slides" description="Slide-by-slide editing is on the way. Your Structure and Message stay the single source of truth for this presentation's text." testId="presentation-slides-area" />
        )}
        {areaKey === "performance-map" && (
          <PlaceholderArea title="Performance Map" description="A map of how this presentation connects to your practice and sessions is on the way." testId="presentation-performance-map-area" />
        )}
        {areaKey === "files" && <FilesArea workspaceId={workspaceId} presId={presId} presentationTitle={data?.project?.title} />}
        {areaKey === "versions" && <VersionHistoryArea workspaceId={workspaceId} presId={presId} />}
      </div>
    </ClientShell>
  );
}
